/**
 * Rehab summary drawer — opened from "View rehab summary" on the Rehab card in
 * the property detail view. Shows the budget-vs-actual headline stats, the rehab
 * timeline, and a cost breakdown by scope item.
 *
 * Cost breakdown rule: when the rehab is COMPLETE, every scoped line item is
 * listed with its spend. When the rehab is still IN PROGRESS, only the items
 * that have actually been completed are listed — each with the date it was
 * finished — so the drawer reflects work done to date rather than the full plan.
 */
import { CheckCircle2, ExternalLink, Wrench } from "lucide-react";
import { differenceInDays, format, parseISO } from "date-fns";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { fmtMoney } from "@/lib/calculators";
import type { RehabLineItemRow } from "@/types/pipeline";

interface RehabSummaryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  address: string;
  estimated: number;
  actual: number;
  isComplete: boolean;
  /** "YYYY-MM-DD" acquisition / rehab start date. */
  startDate: string | null;
  /** ISO completion date (latest line-item completion), null while in progress. */
  completedDate: string | null;
  /** All rehab line items for the property. */
  items: RehabLineItemRow[];
  onOpenRehabPage: () => void;
}

const itemAmount = (i: RehabLineItemRow): number =>
  Number(i.actual_cost) || Number(i.estimated_cost) || 0;

const isItemDone = (i: RehabLineItemRow): boolean =>
  i.status === "completed" && Boolean(i.completed_at);

const fmtDate = (date: string | null): string =>
  date ? format(parseISO(date), "MMM d, yyyy") : "—";

export function RehabSummaryDrawer({
  open,
  onOpenChange,
  address,
  estimated,
  actual,
  isComplete,
  startDate,
  completedDate,
  items,
  onOpenRehabPage,
}: RehabSummaryDrawerProps) {
  const variance = actual - estimated;
  const overBudget = variance > 0.5;
  const vsBudgetPct = estimated > 0 ? (variance / estimated) * 100 : 0;

  // Complete → every scoped item; in progress → only the finished ones (each
  // shown with its completion date), per the drawer's breakdown rule.
  const scopeItems = (
    isComplete ? items.filter((i) => itemAmount(i) > 0) : items.filter(isItemDone)
  ).slice();
  const totalSpend = scopeItems.reduce((s, i) => s + itemAmount(i), 0);

  const completedCount = items.filter(isItemDone).length;
  const progress = isComplete
    ? 100
    : items.length > 0
      ? Math.round((completedCount / items.length) * 100)
      : 0;
  const durationDays =
    startDate && completedDate
      ? differenceInDays(parseISO(completedDate), parseISO(startDate))
      : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b border-border p-6">
          <SheetTitle className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-warning" /> Rehab summary
          </SheetTitle>
          <SheetDescription>
            {address} · {isComplete ? "rehab complete" : "rehab in progress"}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-y-auto p-6">
          {/* Headline stats */}
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Estimated" value={fmtMoney(estimated)} />
            <Stat label="Actual spend" value={fmtMoney(actual)} tone="success" />
            <Stat
              label="Variance"
              value={
                Math.abs(variance) < 0.5
                  ? fmtMoney(0)
                  : `${overBudget ? "+" : "−"}${fmtMoney(Math.abs(variance))}`
              }
              tone={overBudget ? "destructive" : "success"}
            />
            <Stat
              label="Vs budget"
              value={`${Math.abs(vsBudgetPct).toFixed(1)}% ${overBudget ? "over" : "under"}`}
              tone={overBudget ? "destructive" : "warning"}
            />
          </div>

          {/* Timeline */}
          <section>
            <SectionLabel>Timeline</SectionLabel>
            <TimelineRow label="Started" value={fmtDate(startDate)} />
            <TimelineRow
              label="Completed"
              value={isComplete ? fmtDate(completedDate) : "In progress"}
            />
            <TimelineRow
              label="Duration"
              value={durationDays != null ? `${durationDays} days` : "—"}
            />
            <TimelineRow label="Progress" value={`${progress}% complete`} />
          </section>

          {/* Cost breakdown by scope */}
          <section>
            <SectionLabel>Cost breakdown by scope</SectionLabel>
            {scopeItems.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">
                No completed rehab items yet.
              </p>
            ) : (
              <>
                {scopeItems.map((i) => (
                  <div
                    key={i.id}
                    className="flex items-center gap-3 border-b border-border py-2.5 text-sm last:border-b-0"
                  >
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-foreground">
                        {i.label ?? "Rehab item"}
                      </div>
                      {/* In-progress view lists each completed item's date. */}
                      {!isComplete && (
                        <div className="text-xs text-muted-foreground">
                          {fmtDate(i.completed_at)}
                        </div>
                      )}
                    </div>
                    <span className="shrink-0 font-semibold tabular-nums text-foreground">
                      {fmtMoney(itemAmount(i))}
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between border-t-2 border-border py-2.5 text-sm font-semibold">
                  <span className="text-foreground">Total rehab spend</span>
                  <span className="tabular-nums text-foreground">
                    {fmtMoney(totalSpend)}
                  </span>
                </div>
              </>
            )}
          </section>
        </div>

        <div className="border-t border-border p-6">
          <Button className="w-full sm:ml-auto sm:w-auto" onClick={onOpenRehabPage}>
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Open Rehab page
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "success" | "destructive" | "warning";
}) {
  return (
    <div className="rounded-lg border border-border bg-muted/40 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-1 text-lg font-bold tabular-nums",
          tone === "success" && "text-success",
          tone === "destructive" && "text-destructive",
          tone === "warning" && "text-warning",
          !tone && "text-muted-foreground",
        )}
      >
        {value}
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  );
}

function TimelineRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-border py-2.5 text-sm last:border-b-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold tabular-nums text-foreground">{value}</span>
    </div>
  );
}
